import { useEffect, useState, type FormEvent } from 'react';
import { useAppStore } from '../../stores/appStore';
import { useSeason } from '../../hooks/useSeason';

export function LoginScreen() {
  const loginPin = useAppStore((s) => s.loginPin);
  const loginUser = useAppStore((s) => s.loginUser);
  const familyName = useAppStore((s) => s.familyName);
  const season = useSeason();
  const [mode, setMode] = useState<'pin' | 'user'>('pin');
  const [pin, setPin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [netHint, setNetHint] = useState('');
  const [awayUrl, setAwayUrl] = useState('');

  // Probe API so user sees connection status before typing PIN
  useEffect(() => {
    let alive = true;
    const probe = async () => {
      try {
        const r = await fetch('/api/health', { cache: 'no-store' });
        if (!alive) return;
        if (r.ok) {
          setNetHint(`Connected · ${location.host}`);
        } else {
          setNetHint(`Server responded ${r.status} · ${location.host}`);
        }
      } catch {
        if (!alive) return;
        const host = location.host;
        if (/^192\.168\.|^10\./.test(host) || host.startsWith('127.')) {
          setNetHint(
            'No server on this Wi‑Fi address. At work: turn Tailscale on, then open http://100.86.217.101:3847'
          );
        } else if (host.includes('ts.net') || host.startsWith('100.')) {
          setNetHint(
            'Tailscale is not connected. Open the Tailscale app, wait until it says connected, then reload.'
          );
        } else {
          setNetHint(
            'Cannot reach the app. At work: turn Tailscale on, then open http://100.86.217.101:3847'
          );
        }
      }
    };
    void probe();
    fetch('/api/access-info', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return;
        const url = d.tailscale || d.magicdns || d.permanent || d.remote || '';
        if (typeof url === 'string' && url.includes('http')) setAwayUrl(url);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Clear broken SW cache that can break off-network API calls
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        // Don't unregister forever — just ensure controller is current
        void regs;
      }
      if (mode === 'pin') {
        await loginPin(pin);
      } else {
        await loginUser(email, password);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  function pressDigit(d: string) {
    if (pin.length >= 8) return;
    setPin((p) => p + d);
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 p-6">
      <div className="w-full max-w-md rounded-3xl border border-slate-700/60 bg-slate-900/80 p-8 shadow-2xl backdrop-blur">
        <div className="mb-8 text-center">
          <div className="mb-3 text-5xl">👨‍👩‍👧‍👦</div>
          <h1 className="text-3xl font-bold tracking-tight text-white">
            {familyName}
          </h1>
          <p className="mt-1 text-slate-400">
            Family Dashboard
            <span className="mx-2 text-slate-600">·</span>
            <span className="font-semibold text-violet-300">{season.label}</span>
          </p>
        </div>

        <div className="mb-6 flex rounded-xl bg-slate-800 p-1">
          <button
            type="button"
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${
              mode === 'pin'
                ? 'bg-violet-600 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
            onClick={() => setMode('pin')}
          >
            Family PIN
          </button>
          <button
            type="button"
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${
              mode === 'user'
                ? 'bg-violet-600 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
            onClick={() => setMode('user')}
          >
            Personal Login
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {mode === 'pin' ? (
            <>
              <div className="flex justify-center gap-2 py-2">
                {Array.from({ length: Math.max(4, pin.length || 4) }).map(
                  (_, i) => (
                    <div
                      key={i}
                      className={`h-3 w-3 rounded-full ${
                        i < pin.length ? 'bg-violet-400' : 'bg-slate-600'
                      }`}
                    />
                  )
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map(
                  (key) =>
                    key === '' ? (
                      <div key="spacer" />
                    ) : (
                      <button
                        key={key}
                        type="button"
                        className="rounded-2xl bg-slate-800 py-4 text-xl font-semibold text-white transition hover:bg-slate-700 active:scale-95"
                        onClick={() => {
                          if (key === '⌫') setPin((p) => p.slice(0, -1));
                          else pressDigit(key);
                        }}
                      >
                        {key}
                      </button>
                    )
                )}
              </div>
              <input type="hidden" value={pin} readOnly />
            </>
          ) : (
            <>
              <label className="block">
                <span className="mb-1 block text-sm text-slate-400">Email</span>
                <input
                  className="w-full rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-white outline-none focus:border-violet-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  placeholder="bobby@fink.local"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm text-slate-400">
                  Password
                </span>
                <input
                  type="password"
                  className="w-full rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-white outline-none focus:border-violet-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </label>
            </>
          )}

          {error && (
            <p className="rounded-lg bg-red-500/15 px-3 py-2 text-center text-sm text-red-300">
              {error}
            </p>
          )}

          {netHint && (
            <p
              className={`rounded-lg px-3 py-2 text-center text-xs ${
                netHint.startsWith('Connected')
                  ? 'bg-emerald-500/10 text-emerald-300'
                  : 'bg-amber-500/10 text-amber-200'
              }`}
            >
              {netHint}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || (mode === 'pin' && pin.length < 4)}
            className="w-full rounded-xl bg-violet-600 py-3.5 text-lg font-bold text-white transition hover:bg-violet-500 disabled:opacity-40"
          >
            {loading ? 'Unlocking…' : 'Unlock'}
          </button>
        </form>

        <p className="mt-4 text-center text-xs leading-relaxed text-slate-500">
          Personal PINs: Bobby <span className="text-slate-300">1988</span> ·
          Candace <span className="text-slate-300">1989</span> · Emma{' '}
          <span className="text-slate-300">2007</span> · Corbin{' '}
          <span className="text-slate-300">2011</span>
          <br />
          TV kiosk shared PIN still works (see setup). At work: turn Tailscale
          on, then open{' '}
          <span className="break-all text-slate-400">
            {awayUrl || 'http://100.86.217.101:3847'}
          </span>
        </p>
        <p className="mt-2 text-center">
          <a
            href="/clear-cache"
            className="text-xs text-violet-400 underline hover:text-violet-300"
          >
            Fix stuck login (clear cache)
          </a>
        </p>
      </div>
    </div>
  );
}
